export type CoverImageSource = 'frame' | 'upload';

export type ClipCoverImage = {
  url: string;
  source: CoverImageSource;
  sourceTime?: number;
};

const VALID_SOURCES: readonly CoverImageSource[] = ['frame', 'upload'];

function isValidSource(value: unknown): value is CoverImageSource {
  return typeof value === 'string' && (VALID_SOURCES as readonly string[]).includes(value);
}

function isValidHttpsUrl(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.startsWith('https://');
}

function isValidSourceTime(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

/**
 * Validates and normalizes an arbitrary value into a `ClipCoverImage`,
 * stripping unknown keys. Returns `null` when the cover is unusable (missing
 * or non-https URL, unknown source). A malformed `sourceTime` is dropped but
 * the rest of the cover is kept.
 */
export function sanitizeCoverImage(raw: unknown): ClipCoverImage | null {
  if (raw === null || typeof raw !== 'object') return null;
  const candidate = raw as Record<string, unknown>;

  if (!isValidHttpsUrl(candidate.url)) return null;
  if (!isValidSource(candidate.source)) return null;

  const cover: ClipCoverImage = { url: candidate.url, source: candidate.source };
  if (isValidSourceTime(candidate.sourceTime)) {
    cover.sourceTime = candidate.sourceTime;
  }
  return cover;
}

export function buildFrameCover(url: string, sourceTime: number): ClipCoverImage {
  return { url, source: 'frame', sourceTime };
}

/** A cover taken from the rendered clip video (no source-video time). */
export function buildRenderedFrameCover(url: string): ClipCoverImage {
  return { url, source: 'frame' };
}

export function buildUploadCover(url: string): ClipCoverImage {
  return { url, source: 'upload' };
}

/** Stable diff key for a cover, independent of key insertion order. `'null'` for null. */
export function serializeCoverImage(cover: ClipCoverImage | null): string {
  if (cover === null) return 'null';
  const sourceTime = cover.sourceTime ?? '';
  return `${cover.source}:${cover.url}:${sourceTime}`;
}

/**
 * The cover a clip currently has: the editor's locally saved override (from a
 * write this session) wins over the fetched clip data — including an explicit
 * `coverImage: null` removal, which is why presence of the key is checked
 * rather than its truthiness.
 */
export function resolveClipCoverImage({
  clipData,
  override,
}: {
  clipData: unknown;
  override?: unknown;
}): ClipCoverImage | null {
  if (override !== null && typeof override === 'object' && 'coverImage' in override) {
    return sanitizeCoverImage((override as { coverImage: unknown }).coverImage);
  }
  if (clipData === null || typeof clipData !== 'object') return null;
  return sanitizeCoverImage((clipData as { coverImage?: unknown }).coverImage);
}
